import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ArrowUp } from 'lucide-react';
import { mensagemDeErro } from '../../nucleo/api';
import { modulosWeb, type PerguntaCopiloto, type RespostaCopiloto } from '../../nucleo/modulos-web';
import { useSessao } from '../../nucleo/sessao';

/**
 * CRM no novo desenho, lote 6 (N9, ficha `demandas/entrada/2026-10-26_malgo_MOD_crm-novo-desenho.md`): o Copiloto de IA no
 * formato do clone do RD (saudação, sugestões por categoria, campo de pergunta), com respostas SIMULADAS: cada módulo publica as
 * perguntas que responde (`copiloto`) a partir dos próprios dados, pela API de sempre e com as permissões da pessoa. Nenhuma
 * IA externa, nenhum custo. Pergunta que nenhum módulo reconhece recebe a lista do que o Copiloto responde hoje.
 */
type Pergunta = PerguntaCopiloto & { slug: string };
type Mensagem = { id: number; de: 'pessoa'; texto: string } | { id: number; de: 'copiloto'; resposta: RespostaCopiloto | null; sugestoes?: Pergunta[]; erro?: string };

/** Sem acento, minúsculas e espaços simples: o que a pergunta digitada e o padrão de cada módulo comparam. */
export const normalizarPergunta = (v: string) =>
  v
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[?!.,;:]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const RE_O_QUE_PODE = /o que (voce|vc) (pode|faz|sabe)|what can you|que puedes|ajuda$/;

/** As perguntas que a pessoa pode fazer (módulo instalado e a permissão de cada uma). */
export function perguntasDoCopiloto(s: { instalado(slug: string): boolean; pode(permissao: string): boolean }, lista = modulosWeb): Pergunta[] {
  const ordem = { duvidas: 0, analise: 1 } as const;
  return lista
    .flatMap((m) => (s.instalado(m.slug) ? (m.copiloto ?? []).filter((p) => s.pode(p.permissao)).map((p) => ({ ...p, slug: m.slug })) : []))
    .sort((a, b) => ordem[a.categoria] - ordem[b.categoria]);
}

/** Qual pergunta responde ao texto: o padrão do módulo ou o próprio texto da sugestão. */
export function perguntaQueCasa(texto: string, perguntas: Pergunta[], rotulo: (p: Pergunta) => string): Pergunta | null {
  const n = normalizarPergunta(texto);
  return perguntas.find((p) => normalizarPergunta(rotulo(p)) === n) ?? perguntas.find((p) => p.casa.test(n)) ?? null;
}

function Resposta({ r, aoNavegar }: { r: RespostaCopiloto; aoNavegar: () => void }) {
  return (
    <>
      <p>{r.texto}</p>
      {r.itens && r.itens.length > 0 && (
        <ul className="mt-2 flex list-disc flex-col gap-1 pl-5">
          {r.itens.map((x, i) => (
            <li key={i}>
              {x.link ? (
                <Link to={x.link} onClick={aoNavegar} className="text-acento-forte hover:underline">
                  {x.rotulo}
                </Link>
              ) : (
                x.rotulo
              )}
              {x.valor && <span className="numero font-semibold">: {x.valor}</span>}
            </li>
          ))}
        </ul>
      )}
      {r.link && (
        <Link to={r.link.para} onClick={aoNavegar} className="mt-2 inline-block font-medium text-acento-forte hover:underline">
          {r.link.rotulo}
        </Link>
      )}
    </>
  );
}

/**
 * Ajustes do CRM 27/10 (K12): a conversa do Copiloto (saudação, sugestões, mensagens e o campo de pergunta), dentro do
 * painel "Malgo IA" da casca nova (aba Copiloto). `aoNavegar` fecha o painel quando a resposta leva a outra tela.
 */
export function ConversaCopiloto({ aoNavegar }: { aoNavegar: () => void }) {
  const { t, i18n } = useTranslation();
  const s = useSessao();
  const [texto, setTexto] = useState('');
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [pensando, setPensando] = useState(false);
  const fim = useRef<HTMLDivElement>(null);
  const seq = useRef(0);
  const perguntas = useMemo(() => perguntasDoCopiloto(s), [s]);
  const rotulo = (p: Pergunta) => t(`${p.slug}:${p.pergunta}`);
  useEffect(() => {
    if (mensagens.length || pensando) fim.current?.scrollIntoView?.({ block: 'end' });
  }, [mensagens, pensando]);

  async function perguntar(pergunta: string) {
    const q = pergunta.trim();
    if (!q || pensando) return;
    setTexto('');
    setMensagens((m) => [...m, { id: ++seq.current, de: 'pessoa', texto: q }]);
    setPensando(true);
    const espera = new Promise((r) => setTimeout(r, 500));
    let msg: Mensagem;
    if (RE_O_QUE_PODE.test(normalizarPergunta(q)) || normalizarPergunta(q) === normalizarPergunta(t('casca.copilotoOQuePode'))) {
      msg = { id: ++seq.current, de: 'copiloto', resposta: { texto: t('casca.copilotoPode') }, sugestoes: perguntas };
    } else {
      const p = perguntaQueCasa(q, perguntas, rotulo);
      if (!p) msg = { id: ++seq.current, de: 'copiloto', resposta: { texto: t('casca.copilotoNaoSei') }, sugestoes: perguntas };
      else {
        try {
          msg = { id: ++seq.current, de: 'copiloto', resposta: await p.responder(i18n.getFixedT(null, p.slug)) };
        } catch (e) {
          msg = { id: ++seq.current, de: 'copiloto', resposta: null, erro: mensagemDeErro(e) };
        }
      }
    }
    await espera;
    setMensagens((m) => [...m, msg]);
    setPensando(false);
  }

  const cartao = (p: Pergunta | 'oquepode') => {
    const pergunta = p === 'oquepode' ? t('casca.copilotoOQuePode') : rotulo(p);
    const categoria = p === 'oquepode' ? 'conhecendo' : p.categoria;
    return (
      <button key={p === 'oquepode' ? p : `${p.slug}.${p.chave}`} type="button" data-sugestao-copiloto="" onClick={() => void perguntar(pergunta)} className="flex flex-col gap-1 rounded-xl border border-borda bg-sup-1 p-3 text-left text-sm text-tinta-1 hover:border-acento-forte hover:bg-sup-2">
        <span className="kicker">{t(`casca.copilotoCategorias.${categoria}`)}</span>
        {pergunta}
      </button>
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div data-copiloto="" className="flex flex-col gap-3 text-sm" aria-live="polite">
        {mensagens.length === 0 && (
          <>
            <h3 className="text-xl font-semibold text-tinta-1">
              {t('casca.copilotoOla', { nome: s.usuario.nome.split(' ')[0] })}
              <br />
              {t('casca.copilotoAjuda')}
            </h3>
            <div className="grid gap-2 sm:grid-cols-2">{[cartao('oquepode'), ...perguntas.map(cartao)]}</div>
          </>
        )}
        {mensagens.map((m) =>
          m.de === 'pessoa' ? (
            <p key={m.id} data-msg-pessoa="" className="ml-auto max-w-md rounded-2xl rounded-br-sm bg-acento-suave px-3 py-2 text-tinta-1">
              {m.texto}
            </p>
          ) : (
            <div key={m.id} data-msg-copiloto="" className="w-full rounded-2xl rounded-bl-sm border border-borda bg-sup-1 px-3 py-2 text-tinta-1">
              {m.erro ? <p className="text-perigo">{m.erro}</p> : m.resposta && <Resposta r={m.resposta} aoNavegar={aoNavegar} />}
              {m.sugestoes && m.sugestoes.length > 0 && <div className="mt-2 grid gap-2">{m.sugestoes.map(cartao)}</div>}
            </div>
          ),
        )}
        {pensando && (
          <p role="status" className="text-tinta-3">
            {t('casca.copilotoPensando')}
          </p>
        )}
        <div ref={fim} />
      </div>
      <form
        className="sticky bottom-0 flex w-full flex-col gap-1 border-t border-borda bg-sup-1 pt-3"
        onSubmit={(e) => {
          e.preventDefault();
          void perguntar(texto);
        }}
      >
        <div className="relative">
          <input
            data-pergunta-copiloto=""
            aria-label={t('casca.copilotoPergunta')}
            placeholder={t('casca.copilotoPergunta')}
            maxLength={300}
            autoComplete="off"
            value={texto}
            onChange={(e) => setTexto(e.target.value)}
            className="h-11 w-full min-w-0 rounded-full border border-borda-campo bg-campo pr-12 pl-4 text-sm text-tinta-1 focus:border-foco focus:outline-2 focus:outline-offset-0 focus:outline-foco"
          />
          <button type="submit" aria-label={t('casca.copilotoEnviar')} title={t('casca.copilotoEnviar')} disabled={!texto.trim() || pensando} className="absolute top-1/2 right-1.5 flex size-8 -translate-y-1/2 items-center justify-center rounded-full bg-acento text-acento-tinta hover:bg-acento-hover disabled:opacity-40">
            <ArrowUp className="size-4" aria-hidden />
          </button>
        </div>
        <small className="text-xs text-tinta-3">{t('casca.copilotoSimulado')}</small>
      </form>
    </div>
  );
}
